/**
 * KnowledgeBaseGallery Component
 *
 * Full knowledge-base index pattern: search + category filters, featured
 * article, latest articles and card gallery. The same implementation is used
 * by wizard preview, editor preview and published pages.
 */
import { SearchIcon } from '@blocksuite/icons/rc';
import {
  CONTENT_I18N_ROW_STATE_KEY,
  extractKbArticlePlainText,
  parseContentI18nRowState,
  safeParseKbArticleBlockDocument,
} from '@streamlain/view-config';
import {
  type ChangeEvent,
  createElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  BoundDataRow,
  ViewComponent,
  ViewComponentRenderProps,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { ResponsiveImage } from '../../responsive-image/responsive-image';
import { useViewNavigationState } from '../../view-renderer/view-navigation-state';
import { BreadcrumbComponent } from '../navigation/breadcrumb';
import { resolveCustomAppRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';
import { getContentCopy } from './content-copy';

const configSchema = z.object({
  articlePathPrefix: z.string().default('/kb'),
  searchLabel: z.string().default('Search'),
  searchPlaceholder: z.string().default('Search articles...'),
  clearSearchLabel: z.string().default('Clear search'),
  categoryLabel: z.string().default('Categories'),
  allLabel: z.string().default('All'),
  categoryField: z.string().default('topicId'),
  featuredField: z.string().default('featured'),
  featuredLabel: z.string().default('Featured article'),
  latestLabel: z.string().default('Latest & greatest'),
  allArticlesLabel: z.string().default('All articles'),
  resultsLabel: z.string().default('Results'),
  latestCount: z.number().int().min(0).max(6).default(2),
  showImage: z.boolean().default(true),
  showMeta: z.boolean().default(true),
  showAuthor: z.boolean().default(true),
});

type KnowledgeBaseGalleryConfig = z.infer<typeof configSchema>;

interface ArticleViewModel {
  key: string;
  title: string;
  slug: string | null;
  description: string;
  summary: string | null;
  image: unknown;
  imageAlt: string;
  categoryValues: string[];
  categoryLabels: string[];
  categorySlugs: string[];
  author: string | null;
  readTime: string | null;
  publishedAt: string | null;
  featured: boolean;
  searchIndex: string;
  fallbackLabel: string | null;
}

interface TopicRouteFilter {
  values: string[];
  label: string;
  description: string | null;
}

function KnowledgeBaseGalleryRenderer({
  config,
  data,
  dataBindingConfig,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const typedConfig = config as KnowledgeBaseGalleryConfig;
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const setBreadcrumb = useViewNavigationState()?.setBreadcrumb;
  const workspaceId = readWorkspaceId(runtimeContext);
  const localizedConfig = useMemo(
    () => localizeBuiltInLabels(typedConfig, runtimeContext?.locale),
    [runtimeContext?.locale, typedConfig]
  );
  const articles = useMemo(
    () =>
      data.map((row, index) =>
        toArticle(row, typedConfig, index, runtimeContext)
      ),
    [data, runtimeContext, typedConfig]
  );
  const categories = useMemo(() => collectCategories(articles), [articles]);
  const topicPathPattern = resolveTopicPathPattern(runtimeContext);
  const indexPath = resolveKnowledgeBaseIndexPath(runtimeContext);
  const routeTopicFilter = useMemo(
    () => resolveTopicRouteFilter(runtimeContext),
    [runtimeContext]
  );
  const articlePathPrefix = resolveArticlePathPrefix(runtimeContext);
  const effectiveConfig = useMemo(
    () => ({
      ...typedConfig,
      ...localizedConfig,
      articlePathPrefix:
        articlePathPrefix === null
          ? typedConfig.articlePathPrefix
          : articlePathPrefix,
    }),
    [articlePathPrefix, localizedConfig, typedConfig]
  );
  const normalizedQuery = query.trim().toLowerCase();
  const activeCategoryValues = useMemo(
    () =>
      activeCategory !== null
        ? activeCategory
          ? [activeCategory]
          : []
        : (routeTopicFilter?.values ?? []),
    [activeCategory, routeTopicFilter]
  );
  const activeCategoryForSidebar =
    activeCategory === null && routeTopicFilter && categories.length > 0
      ? (categories.find(category =>
          routeTopicFilter.values.includes(category.value)
        )?.value ??
        routeTopicFilter.values[0] ??
        '')
      : (activeCategory ?? '');
  const activeCategoryLabel =
    (activeCategory === null ? routeTopicFilter?.label : null) ??
    categories.find(category => category.value === activeCategory)?.label ??
    formatLabel(activeCategory ?? '');
  useEffect(() => {
    if (!setBreadcrumb) return;
    setBreadcrumb({
      title: activeCategoryForSidebar
        ? activeCategoryLabel
        : runtimeContext?.locale?.toLowerCase().startsWith('de')
          ? 'Startseite'
          : 'Home',
      homePath: indexPath ?? '/',
      isHome: !activeCategoryForSidebar,
    });
    return () => setBreadcrumb(null);
  }, [
    activeCategoryForSidebar,
    activeCategoryLabel,
    indexPath,
    runtimeContext?.locale,
    setBreadcrumb,
  ]);
  const filteredArticles = useMemo(
    () =>
      articles.filter(article => {
        const matchesCategory =
          activeCategoryValues.length === 0 ||
          activeCategoryValues.some(value =>
            article.categoryValues.includes(value)
          );
        const matchesQuery =
          normalizedQuery === '' ||
          article.searchIndex.includes(normalizedQuery);
        return matchesCategory && matchesQuery;
      }),
    [activeCategoryValues, articles, normalizedQuery]
  );

  const isFiltering = normalizedQuery !== '' || activeCategoryValues.length > 0;
  const featuredArticle = isFiltering
    ? null
    : (filteredArticles.find(article => article.featured) ??
      filteredArticles[0] ??
      null);
  const regularArticles = featuredArticle
    ? filteredArticles.filter(article => article.key !== featuredArticle.key)
    : filteredArticles;
  const latestArticles = isFiltering
    ? []
    : regularArticles.slice(0, effectiveConfig.latestCount);
  const allArticles = isFiltering
    ? filteredArticles
    : regularArticles.slice(effectiveConfig.latestCount);
  const emptyMessage = dataBindingConfig?.databaseDocId
    ? getContentCopy(runtimeContext?.locale, 'emptyRows')
    : mode === 'builder'
      ? getContentCopy(runtimeContext?.locale, 'notBound')
      : getContentCopy(runtimeContext?.locale, 'unavailable');

  return createElement(
    'section',
    {
      className: styles.kbGallery,
      'data-sl-role': 'knowledge-base-gallery',
    },
    !setBreadcrumb &&
      (runtimeContext?.page?.['templateKey'] === 'knowledgebase-index' ||
        runtimeContext?.page?.['path'] === '/' ||
        (indexPath !== null && runtimeContext?.page?.['path'] === indexPath))
      ? BreadcrumbComponent.render({
          config: { showHome: false },
          data: [],
          mode,
          runtimeContext,
        })
      : null,
    createElement(
      'div',
      { className: styles.kbGalleryShell, 'data-sl-role': 'kb-gallery-shell' },
      renderSidebar({
        query,
        setQuery,
        activeCategory: activeCategoryForSidebar,
        setActiveCategory,
        categories,
        articleCount: articles.length,
        topicPathPattern,
        indexPath,
        config: effectiveConfig,
        routeCategoryActive: routeTopicFilter !== null,
        runtimeContext,
      }),
      createElement(
        'div',
        { className: styles.kbGalleryMain, 'data-sl-role': 'kb-main' },
        routeTopicFilter && activeCategory === null
          ? renderTopicHeader(routeTopicFilter)
          : createElement(
              'h1',
              {
                className: styles.kbGalleryFeaturedTitle,
                'data-sl-role': 'kb-index-title',
              },
              effectiveConfig.allArticlesLabel
            ),
        filteredArticles.length === 0
          ? renderEmptyState(emptyMessage)
          : isFiltering
            ? [
                renderArticleSection({
                  label: buildResultsLabel(
                    effectiveConfig,
                    query,
                    activeCategoryForSidebar,
                    activeCategoryLabel
                  ),
                  articles: allArticles,
                  config: effectiveConfig,
                  variant: 'grid',
                  workspaceId,
                  runtimeContext,
                }),
              ]
            : [
                featuredArticle
                  ? renderFeaturedArticle(
                      featuredArticle,
                      effectiveConfig,
                      workspaceId,
                      runtimeContext
                    )
                  : null,
                latestArticles.length > 0
                  ? renderArticleSection({
                      label: effectiveConfig.latestLabel,
                      articles: latestArticles,
                      config: effectiveConfig,
                      variant: 'latest',
                      workspaceId,
                      runtimeContext,
                    })
                  : null,
                allArticles.length > 0
                  ? renderArticleSection({
                      label: effectiveConfig.allArticlesLabel,
                      articles: allArticles,
                      config: effectiveConfig,
                      variant: 'grid',
                      workspaceId,
                      runtimeContext,
                    })
                  : null,
              ]
      )
    )
  );
}

function renderSidebar(input: {
  query: string;
  setQuery: (value: string) => void;
  activeCategory: string;
  setActiveCategory: (value: string) => void;
  articleCount: number;
  categories: Array<{
    value: string;
    label: string;
    slug: string;
    count: number;
  }>;
  topicPathPattern: string | null;
  indexPath: string | null;
  config: KnowledgeBaseGalleryConfig;
  routeCategoryActive: boolean;
  runtimeContext?: ViewRuntimeContext;
}) {
  return createElement(
    'aside',
    { className: styles.kbGallerySidebar, 'data-sl-role': 'kb-sidebar' },
    createElement(
      'div',
      {
        className: styles.kbGallerySearchGroup,
        'data-sl-role': 'kb-search-group',
      },
      createElement(
        'label',
        { className: styles.kbGalleryLabel },
        input.config.searchLabel
      ),
      createElement(
        'div',
        { className: styles.kbGallerySearchBox, 'data-sl-role': 'kb-search' },
        createElement(
          'span',
          { className: styles.kbGallerySearchIcon, 'aria-hidden': 'true' },
          createElement(SearchIcon, {
            width: 18,
            height: 18,
            style: { display: 'block', width: 18, height: 18 },
            focusable: 'false',
          })
        ),
        createElement('input', {
          className: styles.kbGallerySearchInput,
          type: 'search',
          value: input.query,
          placeholder: input.config.searchPlaceholder,
          'aria-label': input.config.searchPlaceholder,
          onChange: (event: ChangeEvent<HTMLInputElement>) =>
            input.setQuery(event.target.value),
        }),
        input.query
          ? createElement(
              'button',
              {
                className: styles.kbGallerySearchClear,
                type: 'button',
                'aria-label': input.config.clearSearchLabel,
                onClick: () => input.setQuery(''),
              },
              '×'
            )
          : null
      )
    ),
    createElement(
      'div',
      {
        className: styles.kbGalleryCategoryGroup,
        'data-sl-role': 'kb-categories',
      },
      createElement(
        'div',
        { className: styles.kbGalleryLabel },
        input.config.categoryLabel
      ),
      createElement(
        'div',
        {
          className: styles.kbGalleryCategoryList,
          'data-sl-role': 'kb-category-list',
        },
        renderCategoryButton({
          value: '',
          label: input.config.allLabel,
          count: input.articleCount,
          activeCategory: input.activeCategory,
          setActiveCategory: input.setActiveCategory,
          disabled: false,
          href:
            input.routeCategoryActive && input.indexPath
              ? resolveCustomAppRuntimeHref(
                  input.indexPath,
                  input.runtimeContext
                )
              : null,
        }),
        ...input.categories.map(category =>
          renderCategoryButton({
            ...category,
            activeCategory: input.activeCategory,
            setActiveCategory: input.setActiveCategory,
            disabled: false,
            href: input.topicPathPattern
              ? resolveCustomAppRuntimeHref(
                  buildTopicHref(input.topicPathPattern, category.slug),
                  input.runtimeContext
                )
              : null,
          })
        )
      )
    )
  );
}

function renderCategoryButton(input: {
  value: string;
  label: string;
  count: number;
  activeCategory: string;
  setActiveCategory: (value: string) => void;
  disabled: boolean;
  href: string | null;
}) {
  const interactiveProps = input.href
    ? {
        href: input.href,
        'aria-disabled': input.disabled ? ('true' as const) : undefined,
        onClick: (event: {
          preventDefault: () => void;
          button: number;
          metaKey: boolean;
          ctrlKey: boolean;
          shiftKey: boolean;
          altKey: boolean;
        }) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;
          event.preventDefault();
          if (!input.disabled) input.setActiveCategory(input.value);
        },
      }
    : {
        type: 'button' as const,
        disabled: input.disabled,
        onClick: () => input.setActiveCategory(input.value),
      };
  return createElement(
    input.href ? 'a' : 'button',
    {
      key: input.value || 'all',
      className: styles.kbGalleryCategoryButton,
      'data-active': input.activeCategory === input.value ? 'true' : 'false',
      'data-sl-role': 'kb-category-button',
      ...interactiveProps,
    },
    createElement('span', null, input.label),
    createElement(
      'span',
      {
        className: styles.kbGalleryCategoryCount,
        'data-sl-role': 'kb-category-count',
      },
      input.count
    )
  );
}

function renderTopicHeader(filter: TopicRouteFilter) {
  return createElement(
    'header',
    {
      key: `topic-${filter.label}`,
      className: styles.kbGalleryArticleSection,
      'data-sl-role': 'kb-topic-header',
    },
    createElement(
      'div',
      {
        className: styles.kbGallerySectionLabel,
        'data-sl-role': 'kb-section-label',
      },
      'Topic'
    ),
    createElement(
      'h1',
      { className: styles.kbGalleryFeaturedTitle },
      filter.label
    ),
    filter.description
      ? createElement(
          'p',
          { className: styles.kbGalleryFeaturedDescription },
          filter.description
        )
      : null
  );
}

function renderFeaturedArticle(
  article: ArticleViewModel,
  config: KnowledgeBaseGalleryConfig,
  workspaceId?: string,
  runtimeContext?: ViewRuntimeContext
) {
  return createElement(
    'section',
    {
      key: `featured-${article.key}`,
      className: styles.kbGalleryFeaturedSection,
      'data-sl-role': 'featured-article-section',
    },
    createElement(
      'div',
      {
        className: styles.kbGallerySectionLabel,
        'data-sl-role': 'kb-section-label',
      },
      config.featuredLabel
    ),
    createElement(
      article.slug ? 'a' : 'article',
      {
        href: article.slug
          ? resolveCustomAppRuntimeHref(
              buildArticleHref(article.slug, config),
              runtimeContext
            )
          : undefined,
        className: styles.kbGalleryFeaturedCard,
        'data-sl-role': 'featured-article',
      },
      config.showImage ? renderArticleImage(article, true, workspaceId) : null,
      createElement(
        'div',
        { className: styles.kbGalleryFeaturedContent },
        renderCategoryBadges(article),
        createElement(
          'h2',
          {
            className: styles.kbGalleryFeaturedTitle,
            'data-sl-field': 'title',
          },
          article.title
        ),
        createElement(
          'p',
          {
            className: styles.kbGalleryFeaturedDescription,
            'data-sl-field': 'excerpt',
          },
          article.summary ?? article.description
        ),
        config.showMeta ? renderArticleMeta(article, config, true) : null
      )
    )
  );
}

function renderArticleSection(input: {
  label: string;
  articles: ArticleViewModel[];
  config: KnowledgeBaseGalleryConfig;
  variant: 'latest' | 'grid';
  workspaceId?: string;
  runtimeContext?: ViewRuntimeContext;
}) {
  const gridClassName =
    input.variant === 'latest'
      ? styles.kbGalleryLatestGrid
      : styles.kbGalleryCardGrid;
  return createElement(
    'section',
    { key: input.label, className: styles.kbGalleryArticleSection },
    createElement(
      'div',
      {
        className: styles.kbGallerySectionLabel,
        'data-sl-role': 'kb-section-label',
      },
      input.label
    ),
    createElement(
      'div',
      { className: gridClassName },
      ...input.articles.map(article =>
        renderArticleCard(
          article,
          input.config,
          input.workspaceId,
          input.runtimeContext
        )
      )
    )
  );
}

function renderArticleCard(
  article: ArticleViewModel,
  config: KnowledgeBaseGalleryConfig,
  workspaceId?: string,
  runtimeContext?: ViewRuntimeContext
) {
  return createElement(
    article.slug ? 'a' : 'article',
    {
      key: article.key,
      href: article.slug
        ? resolveCustomAppRuntimeHref(
            buildArticleHref(article.slug, config),
            runtimeContext
          )
        : undefined,
      className: styles.kbGalleryCard,
      'data-sl-role': 'kb-article-card',
    },
    config.showImage ? renderArticleImage(article, false, workspaceId) : null,
    createElement(
      'div',
      { className: styles.kbGalleryCardContent },
      renderCategoryBadges(article),
      createElement(
        'h3',
        { className: styles.kbGalleryCardTitle, 'data-sl-field': 'title' },
        article.title
      ),
      createElement(
        'p',
        {
          className: styles.kbGalleryCardDescription,
          'data-sl-field': 'excerpt',
        },
        article.summary ?? article.description
      ),
      config.showMeta ? renderArticleMeta(article, config, false) : null
    )
  );
}

function renderArticleImage(
  article: ArticleViewModel,
  featured: boolean,
  workspaceId?: string
) {
  const className = featured
    ? styles.kbGalleryFeaturedImage
    : styles.kbGalleryCardImage;
  if (article.image) {
    return createElement(
      'div',
      { className },
      createElement(ResponsiveImage, {
        image: article.image,
        role: featured ? 'hero' : 'card',
        alt: article.imageAlt,
        workspaceId,
        allowWorkspaceBlobFallback: Boolean(workspaceId),
        className: styles.kbGalleryImageFill,
      })
    );
  }
  return createElement(
    'div',
    { className },
    createElement('div', { className: styles.kbGalleryImagePlaceholder })
  );
}

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.customApp?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}

function renderCategoryBadges(article: ArticleViewModel) {
  if (article.categoryLabels.length === 0 && !article.fallbackLabel) {
    return null;
  }
  return createElement(
    'div',
    { className: styles.kbGalleryBadgeStack },
    ...[
      ...article.categoryLabels.slice(0, article.fallbackLabel ? 1 : 2),
      ...(article.fallbackLabel ? [article.fallbackLabel] : []),
    ].map(label =>
      createElement(
        'span',
        { key: label, className: styles.kbGalleryBadge },
        label
      )
    )
  );
}

function renderArticleMeta(
  article: ArticleViewModel,
  config: KnowledgeBaseGalleryConfig,
  featured: boolean
) {
  const metaItems = [
    config.showAuthor ? article.author : null,
    article.readTime,
    article.publishedAt,
  ].filter((value): value is string => Boolean(value));
  if (metaItems.length === 0) return null;
  return createElement(
    'div',
    {
      className: featured
        ? styles.kbGalleryFeaturedMeta
        : styles.kbGalleryCardMeta,
    },
    config.showAuthor && article.author
      ? createElement(
          'span',
          { className: styles.kbGalleryAuthorAvatar, 'aria-hidden': 'true' },
          initials(article.author)
        )
      : null,
    createElement(
      'span',
      { className: styles.kbGalleryMetaText },
      metaItems.join(' · ')
    )
  );
}

function renderEmptyState(message: string) {
  return createElement(
    'div',
    { className: styles.kbGalleryEmpty, 'data-sl-role': 'empty-state' },
    message
  );
}

function toArticle(
  row: BoundDataRow,
  config: KnowledgeBaseGalleryConfig,
  index: number,
  runtimeContext: ViewRuntimeContext | undefined
): ArticleViewModel {
  const title = readString(row, ['title', 'name']) ?? 'Untitled';
  const blockPlainText = readKbBlockPlainText(row);
  const description =
    readString(row, ['description', 'summary', 'excerpt']) ??
    summarizeText(blockPlainText) ??
    'No description yet.';
  const summary =
    readString(row, ['summary', 'excerpt']) ?? summarizeText(blockPlainText);
  const slug = readString(row, ['slug', 'path']);
  const image = readCell(row, ['image', 'cover', 'thumbnail', 'heroImage']);
  const imageAlt = readString(row, ['imageAlt', 'alt', 'coverAlt']) ?? title;
  const categoryValues = resolveArticleCategoryValues(
    row,
    config,
    runtimeContext
  );
  const categoryLabels = categoryValues.map(
    value =>
      resolveRelatedSourceRowLabel(
        runtimeContext?.sources?.['topics'],
        value
      ) ?? formatLabel(value)
  );
  const categorySlugs = categoryValues.map(value =>
    resolveRelatedSourceRowSlug(runtimeContext?.sources?.['topics'], value)
  );
  const author = resolveAuthorName(row, runtimeContext);
  const readTime = readString(row, ['readTime', 'readingTime']);
  const publishedAt = formatDate(readString(row, ['publishedAt', 'date']));
  const featured = readBoolean(readCell(row, [config.featuredField]));
  const i18nState = parseContentI18nRowState(row[CONTENT_I18N_ROW_STATE_KEY]);
  const searchIndex = [
    title,
    description,
    summary,
    blockPlainText,
    categoryLabels.join(' '),
    author,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return {
    key: readString(row, ['id', '_id', 'slug']) ?? `${index}-${title}`,
    title,
    slug,
    description,
    summary,
    image,
    imageAlt,
    categoryValues,
    categoryLabels,
    categorySlugs,
    author,
    readTime,
    publishedAt,
    featured,
    fallbackLabel:
      i18nState?.state === 'fallback'
        ? `${i18nState.resolvedLocale.toUpperCase()} · ${runtimeContext?.locale?.toLowerCase().startsWith('de') ? 'Ersatzsprache' : 'fallback'}`
        : null,
    searchIndex,
  };
}

function readKbBlockPlainText(row: BoundDataRow): string | null {
  const rawValue = row['blocks'];
  if (typeof rawValue === 'string' && rawValue.trim() === '') return null;
  const document = safeParseKbArticleBlockDocument(rawValue);
  if (!document || document.blocks.length === 0) return null;
  const plainText = extractKbArticlePlainText(document).trim();
  return plainText || null;
}

function summarizeText(value: string | null): string | null {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  return normalized.length > 180
    ? `${normalized.slice(0, 177).trimEnd()}...`
    : normalized;
}

function localizeBuiltInLabels(
  config: KnowledgeBaseGalleryConfig,
  locale: string | null | undefined
): KnowledgeBaseGalleryConfig {
  if (!locale?.toLowerCase().startsWith('de')) return config;
  const replacements: Partial<
    Record<keyof KnowledgeBaseGalleryConfig, string>
  > = {
    searchLabel: 'Suche',
    searchPlaceholder: 'Artikel durchsuchen ...',
    clearSearchLabel: 'Suche löschen',
    categoryLabel: 'Kategorien',
    allLabel: 'Alle',
    featuredLabel: 'Empfohlener Artikel',
    latestLabel: 'Neueste Artikel',
    allArticlesLabel: 'Alle Artikel',
    resultsLabel: 'Ergebnisse',
  };
  const englishDefaults = KnowledgeBaseGalleryComponent.defaultConfig;
  return Object.fromEntries(
    Object.entries(config).map(([key, value]) => [
      key,
      typeof value === 'string' &&
      englishDefaults[key as keyof KnowledgeBaseGalleryConfig] === value &&
      replacements[key as keyof KnowledgeBaseGalleryConfig]
        ? replacements[key as keyof KnowledgeBaseGalleryConfig]
        : value,
    ])
  ) as KnowledgeBaseGalleryConfig;
}

function resolveAuthorName(
  row: BoundDataRow,
  runtimeContext: ViewRuntimeContext | undefined
): string | null {
  const authorValue = readCell(row, ['author', 'authorName', 'owner']);
  const relationIds = normalizeStringList(authorValue);
  const authorRowId = relationIds[0] ?? null;
  if (authorRowId) {
    const authorName = resolveRelatedSourceRowLabel(
      runtimeContext?.sources?.['users'],
      authorRowId
    );
    if (authorName) return authorName;
  }
  return readString(row, ['authorName', 'owner']);
}

function resolveRelatedSourceRowLabel(
  source: unknown,
  rowId: string
): string | null {
  if (!source || typeof source !== 'object') return null;
  const record = source as {
    columns?: Array<{ id?: unknown; name?: unknown }>;
    rows?: Array<{ id?: unknown; cells?: Record<string, unknown> }>;
  };
  const row = record.rows?.find(candidate => candidate.id === rowId);
  if (!row) return null;
  const titleColumn = record.columns?.find(column => column.name === 'title');
  const slugColumn = record.columns?.find(column => column.name === 'slug');
  const label =
    readSourceCell(row, titleColumn?.id) ??
    readSourceCell(row, slugColumn?.id) ??
    row.id;
  return typeof label === 'string' && label.trim() ? label.trim() : null;
}

function resolveRelatedSourceRowSlug(source: unknown, rowId: string): string {
  const record = readSourceRecord(source);
  if (!record) return rowId;
  const normalized = rowId.trim().toLowerCase();
  const row = record.rows.find(candidate => {
    if (candidate.id.toLowerCase() === normalized) return true;
    return (
      readNamedSourceCell({ ...candidate, record }, [
        'slug',
        'topicId',
      ])?.toLowerCase() === normalized
    );
  });
  if (!row) return rowId;
  return readNamedSourceCell({ ...row, record }, ['slug', 'topicId']) ?? rowId;
}

function resolveTopicPathPattern(
  runtimeContext: ViewRuntimeContext | undefined
): string | null {
  const page = resolveKnowledgeBaseRoutePage(
    runtimeContext,
    'knowledgebase-topic'
  );
  const path = page?.['path'];
  return typeof path === 'string' && path.includes(':slug') ? path : null;
}

function resolveKnowledgeBaseIndexPath(
  runtimeContext: ViewRuntimeContext | undefined
): string | null {
  const indexPage = resolveKnowledgeBaseRootPage(runtimeContext);
  return typeof indexPage?.['path'] === 'string' ? indexPage['path'] : null;
}

function resolveArticlePathPrefix(
  runtimeContext: ViewRuntimeContext | undefined
): string | null {
  const articlePage = resolveKnowledgeBaseRoutePage(
    runtimeContext,
    'knowledgebase-article'
  );
  const path = articlePage?.['path'];
  if (typeof path !== 'string') return null;
  const prefix = path.replace(/\/:slug(?:\/|$)/, '/').replace(/\/+$/, '');
  return prefix === '/' ? '' : prefix;
}

function resolveKnowledgeBaseRootPage(
  runtimeContext: ViewRuntimeContext | undefined
): Record<string, unknown> | null {
  const pages = readCustomAppPages(runtimeContext);
  const currentPage = runtimeContext?.page;
  if (currentPage?.['templateKey'] === 'knowledgebase-index') {
    return currentPage;
  }
  const parentPageId = currentPage?.['parentPageId'];
  if (typeof parentPageId === 'string') {
    const parent = pages.find(page => page['id'] === parentPageId);
    if (parent?.['templateKey'] === 'knowledgebase-index') return parent;
  }
  const roots = pages.filter(
    page => page['templateKey'] === 'knowledgebase-index'
  );
  return roots.length === 1 ? (roots[0] ?? null) : null;
}

function resolveKnowledgeBaseRoutePage(
  runtimeContext: ViewRuntimeContext | undefined,
  templateKey: 'knowledgebase-article' | 'knowledgebase-topic'
): Record<string, unknown> | null {
  const pages = readCustomAppPages(runtimeContext);
  const root = resolveKnowledgeBaseRootPage(runtimeContext);
  if (!root) return null;
  const rootId = root['id'];
  const candidates = pages.filter(page => page['templateKey'] === templateKey);
  if (typeof rootId === 'string') {
    return candidates.find(page => page['parentPageId'] === rootId) ?? null;
  }
  return candidates.length === 1 ? (candidates[0] ?? null) : null;
}

function readCustomAppPages(
  runtimeContext: ViewRuntimeContext | undefined
): Array<Record<string, unknown>> {
  const customApp = runtimeContext?.customApp;
  const config =
    customApp?.['config'] && typeof customApp['config'] === 'object'
      ? (customApp['config'] as Record<string, unknown>)
      : null;
  const pages = config?.['pages'];
  return Array.isArray(pages)
    ? pages.filter((page): page is Record<string, unknown> =>
        Boolean(page && typeof page === 'object')
      )
    : [];
}

function resolveTopicRouteFilter(
  runtimeContext?: ViewRuntimeContext
): TopicRouteFilter | null {
  const slug = runtimeContext?.routeParams?.['slug']?.trim();
  if (!slug) return null;
  const source = runtimeContext?.sources?.['topics'];
  const topic = findRelatedSourceRowBySlug(source, slug);
  if (!topic) {
    return {
      values: [slug],
      label: formatLabel(slug),
      description: null,
    };
  }

  const title =
    readNamedSourceCell(topic, ['title', 'name', 'label']) ?? formatLabel(slug);
  const topicSlug = readNamedSourceCell(topic, ['slug', 'topicId']);
  const values = [topic.id, topicSlug, slug]
    .filter((value): value is string => Boolean(value))
    .filter((value, index, all) => all.indexOf(value) === index);
  return {
    values,
    label: title,
    description: readNamedSourceCell(topic, ['description', 'summary']),
  };
}

function findRelatedSourceRowBySlug(
  source: unknown,
  slug: string
): SourceRowWithColumns | null {
  const record = readSourceRecord(source);
  if (!record) return null;
  const normalizedSlug = slug.trim().toLowerCase();
  for (const row of record.rows) {
    if (row.id.toLowerCase() === normalizedSlug) return { ...row, record };
    const rowSlug = readNamedSourceCell({ ...row, record }, [
      'slug',
      'topicId',
      'id',
    ]);
    if (rowSlug?.toLowerCase() === normalizedSlug) return { ...row, record };
  }
  return null;
}

interface SourceRecord {
  columns: Array<{ id?: unknown; name?: unknown }>;
  rows: Array<{ id: string; cells?: Record<string, unknown> }>;
}

interface SourceRowWithColumns {
  id: string;
  cells?: Record<string, unknown>;
  record: SourceRecord;
}

function readSourceRecord(source: unknown): SourceRecord | null {
  if (!source || typeof source !== 'object') return null;
  const record = source as {
    columns?: Array<{ id?: unknown; name?: unknown }>;
    rows?: Array<{ id?: unknown; cells?: Record<string, unknown> }>;
  };
  const rows =
    record.rows?.filter(
      (row): row is { id: string; cells?: Record<string, unknown> } =>
        typeof row.id === 'string'
    ) ?? [];
  return {
    columns: record.columns ?? [],
    rows,
  };
}

function readNamedSourceCell(
  row: SourceRowWithColumns,
  names: readonly string[]
): string | null {
  for (const name of names) {
    const column = row.record.columns.find(
      candidate =>
        typeof candidate.name === 'string' &&
        candidate.name.toLowerCase() === name.toLowerCase()
    );
    const value = readSourceCell(row, column?.id);
    const normalized = normalizePrimitiveString(value);
    if (normalized?.trim()) return normalized.trim();
  }
  return null;
}

function readSourceCell(
  row: { cells?: Record<string, unknown> },
  columnId: unknown
): unknown {
  if (typeof columnId !== 'string') return undefined;
  const value = row.cells?.[columnId];
  if (value && typeof value === 'object' && 'value' in value) {
    return (value as { value?: unknown }).value;
  }
  return value;
}

function collectCategories(articles: readonly ArticleViewModel[]) {
  const seen = new Map<
    string,
    { value: string; label: string; slug: string; count: number }
  >();
  for (const article of articles) {
    for (const [index, value] of article.categoryValues.entries()) {
      const existing = seen.get(value);
      if (existing) {
        existing.count += 1;
      } else {
        seen.set(value, {
          value,
          label: article.categoryLabels[index] ?? formatLabel(value),
          slug: article.categorySlugs[index] ?? value,
          count: 1,
        });
      }
    }
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function resolveArticleCategoryValues(
  row: BoundDataRow,
  config: KnowledgeBaseGalleryConfig,
  runtimeContext: ViewRuntimeContext | undefined
): string[] {
  const source = runtimeContext?.sources?.['topics'];
  const relationValues = readStringList(row, ['topic', 'topics']);
  const rawValues =
    relationValues.length > 0
      ? relationValues
      : readStringList(row, [
          config.categoryField,
          'category',
          'categories',
          'tags',
        ]);
  return rawValues
    .map(value => findRelatedSourceRowBySlug(source, value)?.id ?? value)
    .filter((value, index, all) => all.indexOf(value) === index);
}

function buildTopicHref(pattern: string, slug: string): string {
  return pattern.replace(':slug', encodeURIComponent(slug));
}

function readCell(row: BoundDataRow, keys: readonly string[]): unknown {
  for (const key of keys) {
    if (!key) continue;
    const value = row[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return undefined;
}

function readString(row: BoundDataRow, keys: readonly string[]): string | null {
  const value = readCell(row, keys);
  const normalized = normalizePrimitiveString(value);
  return normalized && normalized.trim() ? normalized.trim() : null;
}

function readStringList(row: BoundDataRow, keys: readonly string[]): string[] {
  const raw = readCell(row, keys);
  return normalizeStringList(raw)
    .map(value => value.trim())
    .filter((value, index, all) => value && all.indexOf(value) === index);
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(normalizeStringList);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return normalizeStringList(
      record['name'] ??
        record['title'] ??
        record['label'] ??
        record['value'] ??
        record['id']
    );
  }
  const normalized = normalizePrimitiveString(value);
  return normalized ? normalized.split(',') : [];
}

function normalizePrimitiveString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function readBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value !== 'string') return false;
  return ['1', 'true', 'yes', 'featured'].includes(value.trim().toLowerCase());
}

function formatLabel(value: string): string {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, match => match.toUpperCase());
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(parsed));
}

function buildArticleHref(
  slug: string,
  config: KnowledgeBaseGalleryConfig
): string {
  if (/^https?:\/\//.test(slug) || slug.startsWith('/')) return slug;
  const prefix = config.articlePathPrefix.trim().replace(/\/+$/, '');
  return `${prefix || ''}/${slug.replace(/^\/+/, '')}`;
}

function buildResultsLabel(
  config: KnowledgeBaseGalleryConfig,
  query: string,
  activeCategory: string,
  activeCategoryLabel: string
): string {
  if (query.trim()) return `${config.resultsLabel}: "${query.trim()}"`;
  if (activeCategory) return `${config.resultsLabel}: ${activeCategoryLabel}`;
  return config.resultsLabel;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || 'S';
}

export const KnowledgeBaseGalleryComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.KNOWLEDGE_BASE_GALLERY,
  type: 'content',
  name: 'Knowledge Base Gallery',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    'KB'
  ),
  capabilities: [
    {
      role: 'articles',
      description: 'Knowledge base article rows',
      required: true,
      requirements: [
        { field: 'title', acceptedTypes: ['text'], required: true },
        { field: 'slug', acceptedTypes: ['text'], required: false },
        { field: 'description', acceptedTypes: ['text'], required: false },
        {
          field: 'image',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        {
          field: 'category',
          acceptedTypes: ['text', 'select'],
          required: false,
        },
        { field: 'featured', acceptedTypes: ['checkbox'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    articlePathPrefix: '/kb',
    searchLabel: 'Search',
    searchPlaceholder: 'Search articles...',
    clearSearchLabel: 'Clear search',
    categoryLabel: 'Categories',
    allLabel: 'All',
    categoryField: 'topicId',
    featuredField: 'featured',
    featuredLabel: 'Featured article',
    latestLabel: 'Latest & greatest',
    allArticlesLabel: 'All articles',
    resultsLabel: 'Results',
    latestCount: 2,
    showImage: true,
    showMeta: true,
    showAuthor: true,
  },
  render(props) {
    return createElement(KnowledgeBaseGalleryRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'KB Gallery'
    );
  },
};
